import type { ConversationParticipantEntity } from '@provision/conversation-types';
import type {
  ConversationParticipant,
  CustomAttributeDefinition,
} from '@provision/entity-types';
import { useCallback, useMemo } from 'react';
import type { AddPreSoldMembershipFormValues } from 'src/components/AddPreSoldMembershipModal/add-pre-sold-membership-form';
import type { SupportPolicyFormValues } from 'src/components/Assistant/policies-tab/policy-form';
import type { PublicApiKeyModalProps } from 'src/components/PublicApiKeyModal/public-api-key-modal';
import type { QuickAction } from 'src/components/QuickActions/quick-actions-context';
import type { UpdateUserFormFields } from 'src/components/UpdateUserModal/types';

import type {
  Address,
  Client,
  Macro,
  ManageUserConversationAssigneeGroups,
  MergeClient,
  PreparedConversationParticipantOption,
  Project,
} from 'src/graphql';
import type { BrandWithSupport } from 'src/graphql/entities/brand';
import type { Conversation } from 'src/graphql/entities/conversation/conversation.types';
import type { ConversationAssigneeGroupManagement } from 'src/graphql/entities/conversation-assignee-group';
import type { CustomAttribute } from 'src/graphql/entities/custom-attributes';
import type { PartnerHolidayEntity } from 'src/graphql/entities/partner-holiday/partner-holiday.types';
import type { ScheduleCreator } from 'src/graphql/entities/stripe-subscription-schedule';
import type { NormalizedSupportPolicy } from 'src/graphql/entities/support-policy/support-policy.utils';
import type { ManageUserToUserGroupParams } from 'src/graphql/entities/user-permissions';
import {
  ConditionInput,
  DeepPartial,
  EntityNames,
  NullableDeepPartial,
  SortingInput,
} from 'src/graphql/types';
import type { SignalOnCallScheduleModalProps } from 'src/pages/Settings/signal-oncall-schedule/signal-oncall-schedule-modal';
import type { NewWorkflowModalProps } from 'src/pages/Settings/workflows/components/new-workflow-form';
import type { ClientRaw, ConversationParticipantRaw, PlatformUserRaw } from 'src/types';
import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

export enum Modals {
  SnoozeConversationModal = 'snoozeConversation',
  MergeConversationModal = 'mergeConversation',
  MergeSiteModal = 'mergeSite',
  MergeClientModal = 'mergeClient',
  AssociateParticipantWithClientModal = 'associateParticipantWithClient',
  AddSiteModal = 'addSite',
  AddClientModal = 'addClient',
  AddConversationModal = 'addConversation',
  AddCustomAttributeModal = 'addCustomAttribute',
  ManageCustomAttributGroupModal = 'manageCustomAttributeGroup',
  ContactSupportModal = 'contactSupport',
  MemberServicesFollowUp = 'memberServicesFollowUp',
  SendMembershipEmail = 'sendMembershipEmail',
  AddComplimentaryPeriod = 'addComplimentaryPeriod',
  ActivateTrialMembership = 'activateTrialMembership',
  AddPreSoldMembership = 'addPreSoldMembership',
  AddPartnerDomainModal = 'addPartnerDomain',
  AddCgCustomDomain = 'addCgCustomDomain',
  CreatePartnerModal = 'createPartnerModal',
  UpdateUser = 'updateUser',
  UpdateUserPermissions = 'updateUserPermissions',
  AddProject = 'addProject',
  CreateUser = 'createUser',
  SubmitDay1 = 'submitDay1',
  ResentInviteWithPassword = 'resendInviteWithPassword',
  ProposalWon = 'proposalWon',
  ProposalLost = 'proposalLost',
  QuickActionsModal = 'quickActionsModal',
  MacrosModal = 'macrosModal',
  UserSearchModal = 'userSearchModal',
  UpdateAssigneeGroups = 'updateAssigneeGroups',
  ScheduleHolidays = 'scheduleHolidays',
  UpdateConversationToAddress = 'updateConversationToAddress',
  UpdateClientToAddress = 'updateClientToAddress',
  AiPrompts = 'aiPrompts',
  ShortcutsModal = 'shortcutsModal',
  SelectPartnerModal = 'selectPartnerModal',
  AddressFileRename = 'addressFileRename',
  SignalOnCallScheduleModal = 'signalOnCallScheduleModal',
  SupportPolicyModal = 'supportPolicyModal',
  CopyText = 'copyText',
  CustomSignupLink = 'customSignupLink',
  PauseSubscription = 'pauseSubscription',
  ImportProposals = 'importProposals',
  ImportDtoolsProjects = 'importDtoolsProjects',
  WorkflowAssignments = 'workflowAssignments',
  NewWorkflow = 'newWorkflow',
  ImportPortalioProjects = 'importPortalioProjects',
  ClientSegmentAddModal = 'clientSegmentAddModal',
  ClientSegmentEditModal = 'clientSegmentEditModal',
  ManageAssigneeGroup = 'manageAssigneeGroup',
  PublicApiKey = 'publicApiKey',
}

export type ClientWithPartner = Partial<Omit<Client, 'partner'>> & {
  partner?: {
    id: number;
  } | null;
};

interface ModalsPropsMap {
  [Modals.SnoozeConversationModal]: {
    conversationId?: Conversation['conversationId'];
    conversationStatusId?: Conversation['conversationStatusId'];
    snoozeUntil?: Conversation['snoozeUntil'];
    partnerId?: Conversation['partnerId'];
    friendlyId?: Conversation['friendlyId'];
    subject?: Conversation['subject'];
  };
  [Modals.MergeConversationModal]: {
    conversation?: Conversation;
  };
  [Modals.MergeSiteModal]: {
    address: Address | null;
  };
  [Modals.ShortcutsModal]: null;
  [Modals.SelectPartnerModal]: null;
  [Modals.MergeClientModal]: {
    client: MergeClient;
  };
  [Modals.AssociateParticipantWithClientModal]: {
    participant: DeepPartial<ConversationParticipantRaw>;
    partnerId: number;
  };
  [Modals.ContactSupportModal]: null;
  [Modals.AddCgCustomDomain]: null;
  [Modals.AddSiteModal]: {
    partnerId?: number;
    address?: Address | null;
    client?: {
      partnerId?: number;
      id: string;
      name: string;
      email?: string;
    };
    filters?: ConditionInput[];
    limit?: number;
    sort?: SortingInput;
    offset?: number;
    onSuccessSubmit?: (result: Address) => void;
  } | void | null;
  [Modals.AddCustomAttributeModal]: {
    customAttribute?: CustomAttribute | null;
    customAttributeDefinition?: CustomAttributeDefinition | null;
    entity?: string | null;
  } | void | null;
  [Modals.SignalOnCallScheduleModal]: SignalOnCallScheduleModalProps;
  [Modals.AddClientModal]: {
    partnerId?: number;
    client?: ClientWithPartner | void | null;
    address?: Partial<Address> | void | null;
    participant?: Partial<ConversationParticipant> | void | null;
    onSuccessSubmit?: (result: NullableDeepPartial<ClientRaw>) => void;
    filters?: ConditionInput[];
    limit?: number;
    sort?: SortingInput;
    conversation?: Conversation;
    clientId?: string;
  } | void | null;
  [Modals.AddConversationModal]: {
    requester: {
      id: string;
      label: string;
      participantEntity: ConversationParticipantEntity;
      email?: string;
      data: unknown;
    } | null;
    site?: { id: string; label: string } | null;
  };
  [Modals.MemberServicesFollowUp]: {
    addressId?: string;
    conversationId?: string;
  };
  [Modals.ManageCustomAttributGroupModal]: null;
  [Modals.SendMembershipEmail]: {
    address: Address | void | null;
  };
  [Modals.AddComplimentaryPeriod]: {
    address: Address | null;
    addCppToSchedule: ScheduleCreator;
  } | void | null;
  [Modals.ActivateTrialMembership]: {
    address: Address | null;
  } | void | null;
  [Modals.AddPreSoldMembership]: { addressId: string | null } & (
    | { addPsmToSchedule: ScheduleCreator; onSubmit?: never }
    | { onSubmit: (data: AddPreSoldMembershipFormValues) => void; addPsmToSchedule?: never }
  );
  [Modals.AddPartnerDomainModal]: {
    existingDomains: string[];
    onSubmit: (domain: string) => void;
  };
  [Modals.CreatePartnerModal]: null;
  [Modals.UpdateUser]: {
    user: Partial<PlatformUserRaw>;
    onSubmit: (data: Partial<PlatformUserRaw>) => Promise<unknown> | void;
    disabledFields?: UpdateUserFormFields[];
  };
  [Modals.UpdateUserPermissions]: {
    user: Partial<PlatformUserRaw>;
    onSubmit: (data: ManageUserToUserGroupParams) => void;
  };
  [Modals.AddProject]: {
    address?: Address;
    filters?: ConditionInput[];
    limit?: number;
    sort?: SortingInput;
    offset?: number;
    onSuccessSubmit?: (result: Project) => void;
  } | void | null;
  [Modals.CreateUser]: {
    onSubmit: (data: Partial<PlatformUserRaw>) => Promise<unknown> | void;
    partnerId: number | null;
  };
  [Modals.SubmitDay1]: {
    address: Address | null;
    project: Project | null;
  } | void | null;
  [Modals.ProposalWon]: {
    address: Address | null;
    project: Project | null;
  } | void | null;
  [Modals.ProposalLost]: {
    address: Address | null;
    project: Project | null;
  } | void | null;
  [Modals.ResentInviteWithPassword]: {
    onSubmit: (userId: string, password: string) => void;
    userId: string;
  };
  [Modals.UpdateAssigneeGroups]: {
    user: Partial<PlatformUserRaw>;
    onSubmit: (data: ManageUserConversationAssigneeGroups) => void;
  };
  [Modals.ScheduleHolidays]:
    | {
        updateExisting: true;
        value: PartnerHolidayEntity;
      }
    | {
        updateExisting: false;
        value?: null | undefined;
      };
  [Modals.UpdateClientToAddress]: {
    client: Client;
    onSubmit?: (address: Address) => void;
  };
  [Modals.UpdateConversationToAddress]: {
    conversation: Conversation;
    address?: Address | null;
    onSelect?: (
      addressId: string,
      // think of refactor and reduce number of arguments
      extras: { siteToAdd?: Address; clientAddress?: Address; filters?: ConditionInput[] },
    ) => void;
  };
  [Modals.QuickActionsModal]: {
    actions: QuickAction[];
    isDisabled?: boolean;
  };
  [Modals.MacrosModal]: {
    conversation: Conversation;
    onMacroSelected: (macro: Macro, interpolatedMessage: string) => void;
  };
  [Modals.UserSearchModal]: {
    partnerId: number;
    onSelect: (participant: PreparedConversationParticipantOption | string) => void;
  };
  [Modals.AiPrompts]: {
    onSubmit?: (
      promptId?: string,
      promptName?: string,
      customPrompt?: string,
    ) => Promise<void>;
  };
  [Modals.AddressFileRename]: {
    fileName: string;
    onRename: (newName: string) => void;
  };
  [Modals.SupportPolicyModal]: {
    policy?: Partial<NormalizedSupportPolicy> | null;
    onSave?: (
      values: SupportPolicyFormValues,
      policy?: Partial<NormalizedSupportPolicy> | null,
    ) => void;
  } | null;
  [Modals.CopyText]: {
    title?: string;
    text: string;
    onSuccess?: () => void;
  };
  [Modals.CustomSignupLink]: {
    address: Address | void | null;
    addressSubscriptionId?: string | null;
  };
  [Modals.PauseSubscription]: {
    address: Address | null;
  };
  [Modals.ImportProposals]: {
    onItemsSelected: (
      items: { id: number | string; name: string }[],
      source: 'portalio' | 'dtools',
    ) => void;
  };
  [Modals.ImportDtoolsProjects]: null;
  [Modals.WorkflowAssignments]: null;
  [Modals.NewWorkflow]: NewWorkflowModalProps;
  [Modals.ImportPortalioProjects]: null;
  [Modals.ClientSegmentAddModal]: {
    partnerId: number;
  };
  [Modals.ClientSegmentEditModal]: {
    segment: BrandWithSupport;
    sitesCount: number;
  };
  [Modals.ManageAssigneeGroup]:
    | { mode: 'create'; partnerId: number }
    | { mode: 'edit'; partnerId: number; group: ConversationAssigneeGroupManagement };
  [Modals.PublicApiKey]: PublicApiKeyModalProps;
}

export const ModalsByEntity: Record<
  EntityNames.Client | EntityNames.Site | EntityNames.Project | EntityNames.Conversation,
  Modals
> = {
  [EntityNames.Client]: Modals.AddClientModal,
  [EntityNames.Site]: Modals.AddSiteModal,
  [EntityNames.Project]: Modals.AddProject,
  [EntityNames.Conversation]: Modals.AddConversationModal,
};

type ModalPropsKey = string | symbol | number | ModalsPropsMap[keyof ModalsPropsMap];

interface ModalState {
  modal: Modals | null;
  setModal: (modalName: Modals | null) => void;
  /** Open ModalContainer dialogs, registry or not; returns the call that unregisters it. */
  openDialogCount: number;
  registerOpenDialog: () => () => void;
  modalProps: Record<Modals, Map<ModalPropsKey, ModalsPropsMap[keyof ModalsPropsMap]>>;
  setModalProps: <T extends Modals>(
    modalName: T,
    modalProps: ModalsPropsMap[T],
    modalPropsKey?: ModalPropsKey,
  ) => void;
  deleteModalProps: <T extends Modals>(modalName: T, modalPropsKey: ModalPropsKey) => void;
  closeCurrentModal: () => void;
}

const OPEN_PROPS_KEY = 'openProps';

export const useModalStore = create<ModalState>((set, get) => ({
  modal: null,
  modalProps: {} as ModalState['modalProps'],
  setModal: (modalName) => set({ modal: modalName }),
  openDialogCount: 0,
  registerOpenDialog: () => {
    set((state) => ({ openDialogCount: state.openDialogCount + 1 }));
    return () => set((state) => ({ openDialogCount: state.openDialogCount - 1 }));
  },
  setModalProps: (modalName, modalProps, modalPropsKey) => {
    const key = modalPropsKey || modalProps;
    if (key) {
      const modalPropsMap = get().modalProps?.[modalName];
      const updatedMap = new Map(modalPropsMap);
      updatedMap.set(key, modalProps);
      set({ modalProps: { ...get().modalProps, [modalName]: updatedMap } });
    }
  },
  deleteModalProps: (modalName, modalPropsKey) => {
    const modalPropsMap = get().modalProps?.[modalName];
    const updatedMap = new Map(modalPropsMap);
    updatedMap.delete(modalPropsKey);
    set({ modalProps: { ...get().modalProps, [modalName]: updatedMap } });
  },
  closeCurrentModal: () => {
    const currentModal = get().modal;
    if (currentModal) {
      set({ modal: null });
      get().deleteModalProps(currentModal, OPEN_PROPS_KEY);
    }
  },
}));

export const useModalState = <T extends Modals>(modalId: T) => {
  const { isOpened, modalProps: _modalProps } = useModalStore(
    useShallow((state) => ({
      isOpened: state.modal === modalId,
      modalProps: state.modalProps?.[modalId] || null,
    })),
  );

  const modalProps = useMemo(() => {
    if (!_modalProps) return null;

    if (_modalProps instanceof Map) {
      let result = {};
      _modalProps.forEach((value) => {
        result = { ...result, ...value };
      });
      return result as ModalsPropsMap[T];
    }

    return _modalProps;
  }, [_modalProps]);

  return useMemo(
    () => ({
      isOpened,
      modalProps,
    }),
    [isOpened, modalProps],
  );
};

export const useShowModals = <T extends Modals>(...args: T[]) => {
  const setModal = useModalStore((state) => state.setModal);
  const setModalProps = useModalStore((state) => state.setModalProps);

  return useMemo(
    () =>
      (args || []).map((modalId) => (props?: ModalsPropsMap[T]) => {
        if (props) {
          setModalProps(modalId, props, OPEN_PROPS_KEY);
        }
        setModal(modalId);
      }),
    // this hook has dynamic dependencies based on number
    // of arguments passed to it
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [...args, setModal, setModalProps],
  );
};

export const useModalMethods = <T extends Modals>(modalId: T) => {
  const setModal = useModalStore((state) => state.setModal);
  const _setModalProps = useModalStore((state) => state.setModalProps);
  const deleteModalProps = useModalStore((state) => state.deleteModalProps);
  const [showModal] = useShowModals(modalId);

  const closeModal = useCallback(() => {
    setModal(null);
    deleteModalProps(modalId, OPEN_PROPS_KEY);
  }, [setModal, deleteModalProps, modalId]);

  const setModalProps = useCallback(
    (modalProps: ModalsPropsMap[T], modalPropsKey?: string | symbol | number) => {
      _setModalProps(modalId, modalProps, modalPropsKey);
      return () => {
        deleteModalProps(modalId, modalPropsKey || modalProps);
      };
    },
    [modalId, _setModalProps, deleteModalProps],
  );

  return useMemo(
    () => ({
      showModal,
      setModalProps,
      closeModal,
    }),
    [closeModal, showModal, setModalProps],
  );
};

export const useCreateModalByEntity = (
  modalId:
    | EntityNames.Client
    | EntityNames.Site
    | EntityNames.Project
    | EntityNames.Conversation,
) => {
  const modal = useModalMethods(ModalsByEntity[modalId]);

  return modal;
};
